import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';
import { useNavigate } from 'react-router';
import { newExternalUrl } from 'utils/urlHelpers';
import {
  useAffiliate,
  useApplicationQuery,
  useCreateApplicationWithoutRiskAndSendGACookiesMutation,
  useExternalConsentsDataQuery,
  usePrecheckApplicationMutation,
  useRegistration,
} from 'hooks/webapi';
import useCalculator from 'hooks/calculator/useCalculator';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import useIovation from 'hooks/useIovation';
import { isAlreadyExistErrorMsg } from 'utils/errorMessageValidation';
import { PersonalDetailsFormValues } from 'pages/Registration/PersonalDetails/PersonalDetailsFormValues';
import {
  ValidationError,
  formatBackendValidationErrors,
  scrollToElement,
} from 'utils';
import {
  PHONE_VERIFICATION,
  REJECTED_OFFER,
  MANUAL_REVIEW,
  PERSONAL_DETAILS_CONTINUE,
} from 'Router/routes';
import validatePersonalDetailsForm from './validatePersonalDetailsForm';
import useSeon from 'hooks/useSeon';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import IdentifyEvent from 'hooks/context/EventCollector/events/IdentifyEvent';
import useAuth from 'utils/auth';
import { Consent } from 'hooks/webapi/useRegistration';
import { DASHBOARD_LOGIN, PREFIX_PHONE } from '../../../constants';
import { useFormik } from 'formik';

export default function usePersonalDetails() {
  const [pending, setPending] = useState(false);
  const { loginWithKeycloakLink } = useAuth();
  const { partialRegister } = useRegistration();
  const { mutateAsync: precheckApplication } = usePrecheckApplicationMutation();
  const { mutateAsync: createClientApplicationWithoutRisk } =
    useCreateApplicationWithoutRiskAndSendGACookiesMutation();
  const { amount, offer } = useCalculator();
  const [isPollingApplicationStatus, setIsPollingApplicationStatus] =
    useState(false);
  const { data: applicationCheckStatusData } =
    useClientApplicationCheckStatusQuery(isPollingApplicationStatus, 3000);
  const { data: applicationData } = useApplicationQuery(
    applicationCheckStatusData?.completed,
  );
  const navigate = useNavigate();
  const { ioBlackBox } = useIovation();
  const { identify } = useEventCollector();
  const { createSeonScript } = useSeon();

  const { affiliate, affiliateForm } = useAffiliate();
  const [isAccountAlreadyExistsError, setIsAccountAlreadyExistsError] =
    useState(false);
  const { data: availableConsents, isLoading } = useExternalConsentsDataQuery();

  const handleSubmit = useCallback(
    async (
      values: PersonalDetailsFormValues,
      actions: FormikHelpers<PersonalDetailsFormValues>,
    ) => {
      try {
        setPending(true);
        // const recaptchaToken = await handleRecaptchaVerify();
        const { email, password } = values;

        const sanitizedValues: PersonalDetailsFormValues & {
          consents: Consent[];
        } = {
          ...values,
          mobilePhone: values.mobilePhone
            .replace(/ /g, '')
            .replace(PREFIX_PHONE, ''),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          passwordRepeat: values.password,
          consents: [
            {
              name: 'ACCEPT_CREDIT_AGREEMENT',
              accepted: values.acceptAgreement,
              version: availableConsents?.ACCEPT_CREDIT_AGREEMENT.version,
            },
            {
              name: 'UNDERSTANDING_AGREEMENT_CONFIRMED',
              accepted: values.acceptProfiling,
              version:
                availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.version,
            },
            {
              name: 'ACCEPT_NEWS',
              accepted: values.acceptNews,
              version: availableConsents?.ACCEPT_NEWS.version,
            },
          ],
        };

        const clientData = await partialRegister(
          { body: sanitizedValues } /* recaptchaToken */,
        );

        const nextPathUrl = `${PERSONAL_DETAILS_CONTINUE}?amount=${amount}`;
        const callbackUrl = newExternalUrl(nextPathUrl);

        const loginData = await loginWithKeycloakLink(
          email,
          password,
          clientData.clientUuid,
          callbackUrl,
        );

        setPending(false);
        if (loginData) window.location.assign(loginData.link);
      } catch (e: unknown) {
        if (
          e !== null &&
          e instanceof ValidationError &&
          e?.fieldErrors?.length
        ) {
          const isAlreadyExistError = isAlreadyExistErrorMsg(e);

          if (isAlreadyExistError) {
            setIsAccountAlreadyExistsError(true);
            window.scrollTo(0, 0);
            setPending(false);
            return;
          }

          const errors = formatBackendValidationErrors(e.fieldErrors);

          actions.setErrors(errors);
          scrollToElement(e.fieldErrors[0].property);
        } else {
          // eslint-disable-next-line no-console
          console.log(e);
        }

        setPending(false);
      }
    },
    [
      availableConsents?.ACCEPT_CREDIT_AGREEMENT.version,
      availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.version,
      availableConsents?.ACCEPT_NEWS.version,
      partialRegister,
      loginWithKeycloakLink,
      offer,
      createClientApplicationWithoutRisk,
      precheckApplication,
    ],
  );

  const handleLoginClick = useCallback((email: string) => {
    window.location.assign(
      `${DASHBOARD_LOGIN}?${import.meta.env.VITE_DASHBOARD_LOGIN_SEARCH_PARAMETER}=${window.btoa(
        email,
      )}`,
    );
  }, []);

  useEffect(() => {
    if (applicationCheckStatusData?.completed) {
      // We can stop polling the status check, when we have result
      setIsPollingApplicationStatus(false);
    }
  }, [applicationCheckStatusData]);

  useEffect(() => {
    if (!applicationData) {
      return;
    }
    identify(new IdentifyEvent({ application: applicationData }));
    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution === 'MANUAL'
    ) {
      navigate(MANUAL_REVIEW);
    }

    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution !== 'MANUAL'
    ) {
      navigate(PHONE_VERIFICATION);
      return;
    }

    if (applicationData.resolution === 'REJECTED') {
      navigate(REJECTED_OFFER);
    }
  }, [applicationData, navigate]);

  const handleAlreadyExistBoxClose = useCallback(() => {
    setIsAccountAlreadyExistsError(false);
  }, []);

  const formik = useFormik({
    initialValues: {
      affiliateInfo: affiliate,
      firstName: '',
      lastName: '',
      mobilePhone: '',
      personalId: '',
      email: '',
      password: '',
      passwordRepeat: '',
      acceptAgreement: false,
      acceptProfiling: false,
      acceptNews: false,
      acceptFaceIdProcessing: false,
      source: 'DESKTOP',
      ioBlackBox: '',
    },
    onSubmit: handleSubmit,
    validate: validatePersonalDetailsForm,
  });

  useEffect(() => {
    formik.setFieldValue('ioBlackBox', ioBlackBox);
  }, [ioBlackBox]);
  useEffect(() => createSeonScript(), []);

  useEffect(() => {
    (async () => {
      if (affiliateForm) {
        Object.keys(affiliateForm).forEach((val) =>
          formik.setFieldTouched(val, true),
        );
        await formik.setValues({
          ...formik.values,
          ...affiliateForm,
        });
      }
    })();
  }, [affiliateForm]);

  useEffect(() => {
    if (formik.isSubmitting) {
      const error = Object.entries(formik.errors).find(
        (key) => key[1] !== undefined,
      );

      if (error) {
        scrollToElement(error[0]);
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  const handleCheckboxChangeWithTouched = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      formik.setFieldTouched(e.target.name);
      formik.handleChange(e);
    },
    [formik],
  );

  return {
    handleCheckboxChangeWithTouched,
    formik,
    pending,
    handleSubmit,
    ioBlackBox,
    affiliateForm,
    isAccountAlreadyExistsError,
    affiliate,
    handleLoginClick,
    handleAlreadyExistBoxClose,
    availableConsents,
    availiableConsentsLoading: isLoading,
  };
}
